import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Section, Container, Eyebrow, RevealHeading, Reveal, TextLink } from "@/components/primitives";
import { FlowDiagram } from "@/components/FlowDiagram";
import { CTASection } from "@/components/CTASection";
import { Seo } from "@/components/Seo";
import { SERVICES, PRACTICES } from "@/data/services";

const ListBlock = ({ n, label, items, dark }) => (
  <Reveal>
    <div className="border-t border-govx-charcoal/10 pt-6">
      <div className="flex items-center gap-3">
        <span className="font-mono text-xs text-govx-green">{n}</span>
        <span className="eyebrow text-govx-grey">{label}</span>
      </div>
      <ul className="mt-5 space-y-3">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2.5 text-[0.95rem]">
            <Check className="mt-1 h-4 w-4 shrink-0 text-govx-green" />
            <span className={dark ? "text-govx-paper/85" : "text-govx-charcoal/80"}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  </Reveal>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICES[slug];
  if (!s) return <Navigate to="/what-we-do" replace />;
  const practice = PRACTICES[s.practice];

  return (
    <>
      <Seo title={`${s.title} | GovX ${practice.label}`} description={s.what} image={s.image} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-govx-black pt-[74px] text-govx-paper grain">
        <Container className="relative z-10 grid gap-12 py-20 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:py-24">
          <div>
            <Link to={practice.to} className="inline-flex items-center gap-2 font-mono text-[0.62rem] uppercase tracking-[0.18em] text-govx-lime hover:text-govx-paper">
              GovX {practice.label}
            </Link>
            <RevealHeading
              lines={[s.title]}
              className="mt-6 font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
            />
            <Reveal delay={0.2}>
              <p className="mt-5 text-lg font-medium text-govx-lime">{s.tagline}</p>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-govx-grey sm:text-lg">{s.what}</p>
            </Reveal>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[4/3] overflow-hidden border border-white/10"
          >
            <img src={s.image} alt={s.title} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-govx-black/60 to-transparent" />
          </motion.div>
        </Container>
      </section>

      {/* Problem + How we work */}
      <Section className="py-20 lg:py-28">
        <Container className="grid gap-14 lg:grid-cols-[1fr_0.9fr]">
          <div>
            <Eyebrow>The Problem</Eyebrow>
            <RevealHeading
              lines={["Where It Usually", "Breaks Down."]}
              as="h2"
              className="mt-6 font-display text-3xl font-medium leading-[1.05] tracking-tight sm:text-4xl"
            />
            <div className="mt-8 space-y-0 border-t border-govx-charcoal/10">
              {s.problem.map((p, i) => (
                <Reveal key={p} delay={i * 0.05}>
                  <div className="flex items-start gap-4 border-b border-govx-charcoal/10 py-4">
                    <span className="mt-1 font-mono text-xs text-govx-green">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[0.98rem] text-govx-charcoal/80">{p}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.15}>
            <div className="border border-govx-charcoal/10 bg-white p-8 lg:p-10">
              <Eyebrow>How We Work</Eyebrow>
              <div className="mt-8">
                <FlowDiagram steps={s.how} dark={false} highlightLast />
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* What GovX does + What you get */}
      <Section dark className="py-20 lg:py-28">
        <Container>
          <Eyebrow dark>The Engagement</Eyebrow>
          <RevealHeading
            lines={["What We Do,", "And What You Get."]}
            as="h2"
            className="mt-6 font-display text-3xl font-medium leading-[1.05] tracking-tight sm:text-5xl"
          />
          <div className="mt-14 grid gap-12 md:grid-cols-3">
            <ListBlock n="01" label="What GovX Does" items={s.does} dark />
            <ListBlock n="02" label="What You Get" items={s.get} dark />
            <div>
              <ListBlock n="03" label="Who It's For" items={s.whoFor} dark />
              <Reveal delay={0.1}>
                <div className="mt-8 border border-govx-lime/30 bg-govx-lime/5 p-6">
                  <div className="eyebrow text-govx-grey">The Outcome</div>
                  <p className="mt-3 font-display text-lg font-medium leading-snug text-govx-lime">{s.outcome}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>

      {/* Related */}
      {s.related?.length > 0 && (
        <Section className="py-20 lg:py-24">
          <Container>
            <Eyebrow>Related Capabilities</Eyebrow>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {s.related.map((r) => {
                const rs = SERVICES[r];
                if (!rs) return null;
                return (
                  <Link
                    key={r}
                    to={`/services/${r}`}
                    data-testid={`related-${r}`}
                    className="group flex items-center justify-between border border-govx-charcoal/12 bg-white p-6 transition-colors hover:border-govx-green"
                  >
                    <div>
                      <div className="font-mono text-[0.6rem] uppercase tracking-wider text-govx-green">{PRACTICES[rs.practice].label}</div>
                      <div className="mt-2 font-display text-lg font-medium tracking-tight">{rs.title}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-govx-grey transition-transform group-hover:translate-x-1 group-hover:text-govx-green" />
                  </Link>
                );
              })}
            </div>
            <div className="mt-10">
              <TextLink to={practice.to}>All {practice.label} capabilities</TextLink>
            </div>
          </Container>
        </Section>
      )}

      <CTASection
        eyebrow={`Talk to GovX ${practice.label}`}
        title={["Let's map this", "and build it."]}
        copy={`Tell us about your ${s.title.toLowerCase()} challenge and we'll help you scope it.`}
        cta="Talk to GovX"
      />
    </>
  );
}
